import React, {FocusEvent, MouseEvent, useEffect, useRef} from "react";
import {observer} from "mobx-react";
import {FieldInputContainerComponent} from "../field-input-container.component";
import {FieldEditorCommonProps, FieldInputContainerComponentProps} from "../inputs-common.props";
import {IFormField} from "../../../models/forms/form-field.interface";
import {NullableDate, NullableUndefinedDate} from "../../../types/nullable-types";
import { useMask, MaskOptions, Replacement} from '@react-input/mask';
import {InputBaseBox} from "../../../basic-components/input/input-base.box";
import {useServices} from "../../../hooks/use-services.hook";
import {DateInputParser} from "./date-input-parser";
import {DateInputTracker} from "./date-input-tracker";
import styled from "styled-components";


const InputBox = styled(InputBaseBox)`
    
`

const digitRegEx = /\d/

const maskReplacement = {
    _: digitRegEx,
    ".": /\//,
    s: / /,
    c: /:/
};


export enum DateFieldMask {
    DDMMYYYY = "__.__.____",
    DDMMYYYYhhmm = "__.__.____s__c__",
    DDMMYYYYhhmmss = "__.__.____s__c__c__",
}

const Placeholder = {
    [DateFieldMask.DDMMYYYY]: "dd/mm/yyyy",
    [DateFieldMask.DDMMYYYYhhmm]: "dd/mm/yyyy hh:mm",
    [DateFieldMask.DDMMYYYYhhmmss]: "dd/mm/yyyy hh:mm:ss",
}
interface DateFieldEditorWithInputMaskComponentProps extends FieldEditorCommonProps {
    field: IFormField<NullableDate>;
    mask?: DateFieldMask;
    minDate?: NullableUndefinedDate;
    maxDate?: NullableUndefinedDate
}



export const DateFieldEditorWithInputMaskComponent: React.FC<DateFieldEditorWithInputMaskComponentProps> = observer((props) => {
    const services = useServices();
    const ignoreFieldChangeEventRef = useRef(false);
    const [currentInputValue, setCurrentInputValue] = React.useState<string>("");

    const mask = props.mask ?? DateFieldMask.DDMMYYYY;
    const isReadOnly = Boolean(props.field.isReadOnly || props.isReadonly);

    const maskOptions: MaskOptions = {
        mask: mask,
        replacement: maskReplacement,
        showMask: false,
        separate: true,
        track: params => {
            const inputTracker = new DateInputTracker(params);
            return inputTracker.track();
        }
    };

    const inputRef = useMask(maskOptions);
    const dateParser = useRef(new DateInputParser(services, {
        mask: mask,
        replacement: maskOptions.replacement as Replacement,
        minDate: props.minDate,
        maxDate: props.maxDate,
    }));


    useEffect(() => {
        const subscription = props.field.onChange((newValue) => {
            if(!ignoreFieldChangeEventRef.current) {
                if (newValue) {
                    inputRef.current.value = dateParser.current.formatValue(newValue);
                } else {
                    inputRef.current.value = "";
                }
            }
        });

        if(inputRef.current) {
            props.field.attachInputDOMElement(inputRef.current);
        }


        return () => {
            subscription.unsubscribe();
        }

    }, [inputRef, props.field]);

    const setFieldValue = (inputValue: string, setInputValueAlso: boolean) => {
        ignoreFieldChangeEventRef.current = true;
        try {
            const parseResult = dateParser.current.parseValue(inputValue);
            props.field.setCustomError(parseResult.error);
            props.field.setValue(parseResult.value);
            if(setInputValueAlso) {
                inputRef.current.value = parseResult.formattedValue ?? "";
            }
        } catch(err) {
            services.logger.error('Failed to set date field value', err);
        } finally {
            ignoreFieldChangeEventRef.current = false;
        }
    }

    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {

        if(isReadOnly) {
            event.preventDefault();
            event.stopPropagation();
        } else {
            setFieldValue(event.target.value, false);
            setCurrentInputValue(event.target.value);
        }

    }

    const onBlur = (event: FocusEvent<HTMLInputElement>)=> {
        if(!isReadOnly) {
            setFieldValue(event.target.value, true);
        }
    }


    const renderInput = () => {
        let defaultValue: string = "";
        if(props.field.value) {
            defaultValue = dateParser.current.formatValue(props.field.value);
        }
        return (<InputBox ref={inputRef}
                              placeholder={Placeholder[mask]}
                              defaultValue={defaultValue}
                              readOnly={isReadOnly}
                              onBlur={onBlur}
                              onChange={onChange} />)
    }

    const onClearButtonClick = (event: MouseEvent<HTMLDivElement>)=> {
        if(props.onClearButtonClick) {
            props.onClearButtonClick(event);
        }

        if(event.isDefaultPrevented() || event.isPropagationStopped()) {
            return;
        }

        props.field.clear();
        inputRef.current.value = "";
        setCurrentInputValue("");
    }

    const forceClearButton = props.showClearButton && Boolean(currentInputValue || props.field.value);

    const fieldContainerProps: FieldInputContainerComponentProps = {
        ...props,
        renderInput: renderInput,
        onClearButtonClick: onClearButtonClick,
        forceClearButton: forceClearButton,
        showClearButton: forceClearButton
    }

    
    return (
        <FieldInputContainerComponent {...fieldContainerProps}/>
    );
})


